// src/components/Authentication/SignUpForm/SignUpPage.jsx

import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { SignUpForm } from './SignUpForm';
import { Box, Typography, Grid } from '@mui/material';
import { useStripeSession } from '../../../hooks/useStripe';
import FullScreenLoader from '../../Common/FullScreenLoader';

const subscriptionTiers = {
    basic: { name: 'basic', price: '24.99' },
    pro: { name: 'pro', price: '49.99' },
    default: { name: 'Subscription', price: '0.00' }
};

export default function SignUpPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get params from URL
  const sessionId = searchParams.get('session_id');
  const urlEmail = searchParams.get('email');

  // Fetch session data (if sessionId exists)
  const { isLoading, email: sessionEmail, tier } = useStripeSession(sessionId);

  // Determine the email to pre-fill (Session takes priority, then URL)
  const defaultEmail = sessionEmail || urlEmail || '';

  // Redirect ONLY if we have finished loading AND we lack both a session AND an email
  useEffect(() => {
    if (!isLoading && !sessionId && !urlEmail) {
      window.location.href = "https://candidatetools.com/pricing"; 
    }
  }, [isLoading, sessionId, urlEmail]);

  // Only block render if we are actively loading data
  if (isLoading) {
     return <FullScreenLoader />;
  }

  // If we are not loading, and have neither session nor email, return null (redirecting)
  if (!sessionId && !urlEmail) return null;

  const subscription = subscriptionTiers[tier] || subscriptionTiers.default;

  const authStrings = {
      signUpHeader: "Complete your account",
      signUpSubtitle: sessionId 
        ? "You've successfully subscribed! Just create a password to finish." 
        : "Create your account to get started.",
      emailLabel: "Email",
      passwordLabel: "Password",
      confirmPasswordLabel: "Confirm password",
      passwordsMustMatch: "Passwords must match.",
      switchToSignIn: "Sign in instead",
      submitButton: "Create account"
  };

  return (
    <Grid container component="main" sx={{ height: '100vh' }}>
      {/* Left Column (Branding Panel) */}
      <Grid item xs={12} sm={4} md={5} sx={{ 
            bgcolor: 'primary.main', 
            color: 'primary.contrastText', 
            display: { xs: 'none', sm: 'flex' }, 
            flexDirection: 'column', 
            p: 4, 
            justifyContent: 'space-between' 
        }}
      >
        <Box
          component="img"
          src="ct_logo.png"
          alt="CandidateTools logo"
          sx={{ width: '100%', maxWidth: '250px', height: 'auto', mb: 3 }}
          onError={(e) => { 
            e.target.onerror = null; 
            e.target.src='https://placehold.co/250x60/00897b/FFFFFF?text=CandidateTools&font=roboto'; 
          }}
        />  
        
        {/* Only show pricing info if we actually have a session */}
        {sessionId && (
            <Box>  
                <Typography variant="h5" component="h1" sx={{ mb: 1 }}>
                Subscribed to {subscription.name}
                </Typography>
                <Typography variant="h3" component="p" sx={{ fontWeight: 'bold' }}>
                    ${subscription.price}
                    <Typography component="span" sx={{ fontSize: '1rem', fontWeight: 'normal', ml: 1 }}>
                    per month
                    </Typography>
                </Typography>
            </Box>
        )}
        <Box />
      </Grid>
      
      {/* Right Column (Form Panel) */}
      <Grid item xs={12} sm={8} md={7} component={Box} sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          bgcolor: 'background.paper',
          p: 2
      }}>
        <SignUpForm 
          strings={authStrings}
          defaultEmail={defaultEmail} 
          sessionId={sessionId}
          emailDisabled={!!sessionId} 
        />
      </Grid>
    </Grid>
  );
}